import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Users,
  MessageCircle,
  Phone,
  Mail,
  ExternalLink,
  X,
  User,
  Crown,
  Clock,
} from 'lucide-react';
import clsx from 'clsx';
import { useFeaturedTeam } from '../../services/teamService';
import { getImageUrl } from '../../lib/supabase';

/**
 * Team Section — Masonry grid layout with full-view images.
 * Shows on homepage after testimonials.
 */
export function TeamSection() {
  const { data: members, isLoading } = useFeaturedTeam();
  const [selectedMember, setSelectedMember] = useState(null);

  if (isLoading) {
    return (
      <section className="py-10 sm:py-14 bg-ui-background">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-2 mb-8">
            <div className="w-7 h-7 bg-brand-light rounded animate-pulse" />
            <div className="h-7 bg-brand-light rounded w-40 animate-pulse" />
          </div>
          <div className="columns-1 sm:columns-2 lg:columns-3 gap-4 space-y-4">
            {[1, 2, 3, 4, 5, 6].map((i) => (
              <div key={i} className="break-inside-avoid">
                <div className="bg-brand-light rounded-2xl overflow-hidden animate-pulse" style={{ height: `${200 + Math.random() * 200}px` }} />
              </div>
            ))}
          </div>
        </div>
      </section>
    );
  }

  if (!members || members.length === 0) return null;

  return (
    <section className="py-10 sm:py-14 bg-ui-background">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section header */}
        <motion.div
          initial={{ opacity: 0, y: 15 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.4 }}
          className="flex items-center gap-2 mb-8"
        >
          <Users size={26} className="text-brand-red" />
          <h2 className="text-xl sm:text-2xl font-extrabold text-brand-dark">
            Meet the Team
          </h2>
          <span className="text-sm text-brand-gray ml-1">({members.length})</span>
        </motion.div>

        {/* Masonry Grid */}
        <div className="columns-1 sm:columns-2 lg:columns-3 gap-4 space-y-4">
          {members.map((member, index) => (
            <TeamMemberCard
              key={member.id}
              member={member}
              index={index}
              onClick={() => setSelectedMember(member)}
            />
          ))}
        </div>
      </div>

      {/* Detail Modal */}
      <TeamDetailModal
        member={selectedMember}
        onClose={() => setSelectedMember(null)}
      />
    </section>
  );
}

/* ── Team Member Card ── */
function TeamMemberCard({ member, index, onClick }) {
  const imageUrl = member.image_path ? getImageUrl(member.image_path) : null;
  const isOwner = member.role?.toLowerCase().includes('owner') || member.role?.toLowerCase().includes('founder');

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: '-50px' }}
      transition={{ duration: 0.4, delay: index * 0.05 }}
      className="break-inside-avoid group cursor-pointer"
      onClick={onClick}
    >
      <div className="relative bg-brand-white rounded-2xl border border-ui-border overflow-hidden shadow-sm hover:shadow-lg transition-all duration-300 hover:-translate-y-1">
        {/* Image */}
        <div className="relative w-full overflow-hidden bg-brand-light">
          {imageUrl ? (
            <img
              src={imageUrl}
              alt={member.full_name}
              className="w-full h-auto object-cover object-center group-hover:scale-105 transition-transform duration-500"
              loading="lazy"
            />
          ) : (
            <div className="w-full aspect-[3/4] flex items-center justify-center">
              <User size={80} className="text-ui-muted" />
            </div>
          )}
          
          {/* Gradient overlay */}
          <div className="absolute inset-0 bg-gradient-to-t from-brand-dark/80 via-brand-dark/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
          
          {/* Owner badge */}
          {isOwner && (
            <div className="absolute top-3 left-3 flex items-center gap-1.5 px-2.5 py-1.5 bg-brand-red text-brand-white text-xs font-bold rounded-full shadow-lg">
              <Crown size={11} />
              {member.role}
            </div>
          )}
        </div>

        {/* Info overlay */}
        <div className="p-4">
          <h3 className="text-base sm:text-lg font-extrabold text-brand-dark leading-tight">
            {member.full_name}
          </h3>
          {member.role && !isOwner && (
            <p className="text-xs font-semibold text-brand-red mt-1">{member.role}</p>
          )}
          {member.tagline && (
            <p className="text-xs text-brand-gray mt-2 leading-relaxed line-clamp-2">
              {member.tagline}
            </p>
          )}

          {/* Quick contact actions */}
          <div className="flex items-center gap-2 mt-3 flex-wrap">
            {member.whatsapp && (
              <a
                href={`https://wa.me/${member.whatsapp.replace(/\D/g, '')}`}
                target="_blank"
                rel="noopener noreferrer"
                onClick={(e) => e.stopPropagation()}
                className="inline-flex items-center gap-1 px-3 py-1.5 bg-brand-red hover:bg-red-700 text-brand-white text-xs font-bold rounded-lg transition-colors"
              >
                <MessageCircle size={12} />
                WhatsApp
              </a>
            )}
            {member.phone && (
              <a
                href={`tel:${member.phone}`}
                onClick={(e) => e.stopPropagation()}
                className="inline-flex items-center gap-1 px-3 py-1.5 bg-brand-light hover:bg-gray-200 text-brand-dark text-xs font-bold rounded-lg transition-colors"
                title="Call"
              >
                <Phone size={12} />
              </a>
            )}
            {member.email && (
              <a
                href={`mailto:${member.email}`}
                onClick={(e) => e.stopPropagation()}
                className="inline-flex items-center gap-1 px-3 py-1.5 bg-brand-light hover:bg-gray-200 text-brand-dark text-xs font-bold rounded-lg transition-colors"
                title="Email"
              >
                <Mail size={12} />
              </a>
            )}
          </div>

          {/* Social links */}
          {member.social_links?.length > 0 && (
            <div className="flex items-center gap-1.5 mt-3">
              {member.social_links.map((link, i) => (
                <a
                  key={i}
                  href={link.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={(e) => e.stopPropagation()}
                  className="w-7 h-7 flex items-center justify-center rounded-lg bg-brand-light hover:bg-gray-200 text-brand-gray hover:text-brand-dark transition-colors"
                  title={link.platform}
                >
                  <ExternalLink size={12} />
                </a>
              ))}
            </div>
          )}
        </div>
      </div>
    </motion.div>
  );
}

/* ── Team Member Detail Modal ── */
function TeamDetailModal({ member, onClose }) {
  // Escape key
  useEffect(() => {
    if (!member) return;
    const handle = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handle);
    return () => document.removeEventListener('keydown', handle);
  }, [member, onClose]);

  // Lock body scroll
  useEffect(() => {
    if (member) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [member]);

  const imageUrl = member?.image_path ? getImageUrl(member.image_path) : null;

  return (
    <AnimatePresence>
      {member && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 z-[60] bg-brand-dark/50 backdrop-blur-sm"
          />

          {/* Centering wrapper */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[60] flex items-end sm:items-center justify-center sm:p-6"
            onClick={(e) => e.target === e.currentTarget && onClose()}
          >
            <motion.div
              initial={{ y: 40 }}
              animate={{ y: 0 }}
              exit={{ y: 40 }}
              transition={{ type: 'spring', damping: 30, stiffness: 300 }}
              className="w-full h-full sm:h-auto sm:max-w-2xl sm:max-h-[90vh] bg-brand-white rounded-t-2xl sm:rounded-2xl shadow-2xl overflow-hidden flex flex-col"
              role="dialog"
              aria-modal="true"
              aria-label={`${member.full_name} profile`}
            >
            {/* Close button */}
            <button
              onClick={onClose}
              className="absolute top-4 right-4 z-10 w-8 h-8 flex items-center justify-center rounded-full bg-brand-dark/40 text-brand-white hover:bg-brand-dark/60 transition-colors backdrop-blur-sm"
              aria-label="Close"
            >
              <X size={16} />
            </button>

            <div className="flex-1 overflow-y-auto">
              {/* Header image — compact 16:9 */}
              <div className="relative bg-brand-light overflow-hidden">
                <div className="relative w-full h-44 sm:h-52">
                  {imageUrl ? (
                    <img
                      src={imageUrl}
                      alt={member.full_name}
                      className="absolute inset-0 w-full h-full object-cover object-center"
                      loading="eager"
                    />
                  ) : (
                    <div className="absolute inset-0 w-full h-full flex items-center justify-center">
                      <User size={80} className="text-ui-muted" />
                    </div>
                  )}
                </div>
                <div className="absolute inset-0 bg-gradient-to-t from-brand-dark/60 to-transparent" />
                <div className="absolute bottom-4 left-5">
                  <h2 className="text-xl sm:text-2xl font-extrabold text-brand-white leading-tight">
                    {member.full_name}
                  </h2>
                  {member.role && (
                    <div className="flex items-center gap-1.5 mt-1">
                      {(member.role.toLowerCase().includes('owner') || member.role.toLowerCase().includes('founder')) && (
                        <Crown size={12} className="text-warning" />
                      )}
                      <p className="text-sm font-semibold text-brand-white/90">{member.role}</p>
                    </div>
                  )}
                </div>
              </div>

              {/* Content */}
              <div className="p-5 sm:p-6 space-y-5">
                {/* Tagline */}
                {member.tagline && (
                  <p className="text-sm text-brand-gray italic leading-relaxed border-l-2 border-brand-red pl-3">
                    "{member.tagline}"
                  </p>
                )}

                {/* Bio */}
                {member.bio && (
                  <div>
                    <h4 className="text-xs font-bold text-brand-gray uppercase tracking-wider mb-2">About</h4>
                    <p className="text-sm text-brand-dark leading-relaxed whitespace-pre-line">
                      {member.bio}
                    </p>
                  </div>
                )}

                {/* Contact info */}
                <div>
                  <h4 className="text-xs font-bold text-brand-gray uppercase tracking-wider mb-3">Contact</h4>
                  <div className="space-y-2">
                    {member.whatsapp && (
                      <a
                        href={`https://wa.me/${member.whatsapp.replace(/\D/g, '')}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-3 px-4 py-3 bg-brand-red hover:bg-red-700 text-brand-white text-sm font-bold rounded-xl transition-colors shadow-sm"
                      >
                        <MessageCircle size={16} />
                        Message on WhatsApp
                      </a>
                    )}
                    {member.phone && (
                      <a
                        href={`tel:${member.phone}`}
                        className="flex items-center gap-3 px-4 py-3 bg-brand-light hover:bg-gray-200 text-brand-dark text-sm font-medium rounded-xl transition-colors"
                      >
                        <Phone size={16} className="text-brand-gray" />
                        {member.phone}
                      </a>
                    )}
                    {member.email && (
                      <a
                        href={`mailto:${member.email}`}
                        className="flex items-center gap-3 px-4 py-3 bg-brand-light hover:bg-gray-200 text-brand-dark text-sm font-medium rounded-xl transition-colors"
                      >
                        <Mail size={16} className="text-brand-gray" />
                        {member.email}
                      </a>
                    )}
                  </div>
                </div>

                {/* Social links */}
                {member.social_links?.length > 0 && (
                  <div>
                    <h4 className="text-xs font-bold text-brand-gray uppercase tracking-wider mb-3">Social</h4>
                    <div className="flex items-center gap-2 flex-wrap">
                      {member.social_links.map((link, i) => (
                        <a
                          key={i}
                          href={link.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1.5 px-3 py-2 bg-brand-light hover:bg-gray-200 text-sm font-medium text-brand-dark rounded-xl transition-colors"
                        >
                          <ExternalLink size={14} className="text-brand-gray" />
                          {link.platform}
                        </a>
                      ))}
                    </div>
                  </div>
                )}

                {/* Member since */}
                {member.created_at && (
                  <div className="flex items-center gap-2 text-xs text-ui-muted pt-2 border-t border-ui-border">
                    <Clock size={12} />
                    Member since {new Date(member.created_at).toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
                  </div>
                )}
              </div>
            </div>

            {/* Mobile sticky CTA */}
            {member.whatsapp && (
              <div className="sm:hidden px-5 py-4 border-t border-ui-border bg-brand-white">
                <a
                  href={`https://wa.me/${member.whatsapp.replace(/\D/g, '')}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-center gap-2 w-full py-3 bg-brand-red hover:bg-red-700 text-brand-white text-sm font-bold rounded-xl transition-colors shadow-lg"
                >
                  <MessageCircle size={16} />
                  Message on WhatsApp
                </a>
              </div>
            )}            </motion.div>          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}
